import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Link,
  CircularProgress,
  Alert,
  Tooltip,
  Autocomplete,
  Checkbox
} from '@mui/material';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EngagementDebtChart from './EngagementDebtChart';
import WorkloadChart from './WorkloadChart';
import ExportableChart from './ExportableChart';
import api from '../../lib/api';
import { useDocumentTitle } from '../../hooks';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../i18n/formatters';
import { getCompanyStatus } from '../../i18n/backendTranslations';
import appI18n from '../../i18n/i18n';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import PageLoadingSpinner from '../../components/PageLoadingSpinner';

interface FunktionarEngagement {
    personalNumber: string;
    name: string;
    functionText: string;
    startDate: string | null;
    endDate: string | null;
    organizationNumber: string;
    organizationName: string;
    currentKfmDebt: number | null;
    paymentOrders: string;
    bolagsverketState: string;
    networkCompanyCount: number;
    networkCompaniesActiveDebtCount: number;
    networkCompaniesBankruptcyCount: number;
}

const PersonSearch = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [personalNumber, setPersonalNumber] = useState('');
  const [results, setResults] = useState<FunktionarEngagement[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const roleOptions = useMemo(() => Array.from(new Set(results.map(row => row.functionText))).sort(), [results]);
  const visibleResults = useMemo(() => results.filter(row => selectedRoles.includes(row.functionText)), [results, selectedRoles]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  useDocumentTitle(`${t('personSearch.title')} — Breadify`);

  // Extract search logic to reusable function
  const executeSearch = React.useCallback(async (pnrToSearch: string) => {
    if (!pnrToSearch) return;

    // Clean input (remove hyphens or spaces if any)
    const cleanPersonalNumber = pnrToSearch.replace(/\D/g, '');

    if (cleanPersonalNumber.length !== 12) {
      setError(appI18n.t('personSearch.validation'));
      return;
    }

    // Convert 12 digits to 10 digits for API (remove first 2 digits)
    const apiPersonalNumber = cleanPersonalNumber.substring(2);

    setLoading(true);
    setError(null);
    setResults([]);
    setHasSearched(true);

    try {
      const data = await api.get<FunktionarEngagement[]>(
        `/v1/funktionar/${apiPersonalNumber}/engagements`
      );
      
      // Group by organization
      const groupedByOrg: Record<string, FunktionarEngagement[]> = {};
      data.forEach(item => {
        if (!groupedByOrg[item.organizationNumber]) {
          groupedByOrg[item.organizationNumber] = [];
        }
        groupedByOrg[item.organizationNumber].push(item);
      });

      // Sort organizations
      const sortedOrgNumbers = Object.keys(groupedByOrg).sort((orgA, orgB) => {
        const rolesA = groupedByOrg[orgA];
        const rolesB = groupedByOrg[orgB];

        // Check if org has any active role
        const hasActiveA = rolesA.some(r => r.endDate === null);
        const hasActiveB = rolesB.some(r => r.endDate === null);

        if (hasActiveA && !hasActiveB) return -1;
        if (!hasActiveA && hasActiveB) return 1;

        // If both have active or both don't, sort by most recent start date
        const maxDateA = Math.max(...rolesA.map(r => r.startDate ? new Date(r.startDate).getTime() : 0));
        const maxDateB = Math.max(...rolesB.map(r => r.startDate ? new Date(r.startDate).getTime() : 0));
        
        return maxDateB - maxDateA;
      });

      // Flatten and sort within groups
      const sortedData: FunktionarEngagement[] = [];
      sortedOrgNumbers.forEach(orgNum => {
        const roles = groupedByOrg[orgNum];
        
        // Sort roles within organization
        roles.sort((a, b) => {
            // Sort by null end date (active first)
            if (a.endDate === null && b.endDate !== null) return -1;
            if (a.endDate !== null && b.endDate === null) return 1;
            
            // Sort by start date descending
            const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
            const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
            return dateB - dateA;
        });
        
        sortedData.push(...roles);
      });

      setResults(sortedData);
      setSelectedRoles(Array.from(new Set(sortedData.map(row => row.functionText))));
    } catch (err: any) {
      setError(err.message || appI18n.t('personSearch.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = () => {
    if (!loading) executeSearch(personalNumber);
  };

  // Check for 'pnr' query param on mount
  useEffect(() => {
    const pnrParam = searchParams.get('pnr');
    if (pnrParam) {
      setPersonalNumber(pnrParam);
      executeSearch(pnrParam);
    }
  }, [executeSearch, searchParams]);

  // Calculate shared time domain for charts
  const today = new Date().getTime();
  let minDate = today;
  let maxDate = 0;
  let hasActiveRoles = false;

  visibleResults.forEach(item => {
       const start = item.startDate ? new Date(item.startDate).getTime() : today;
       const end = item.endDate ? new Date(item.endDate).getTime() : today;
       if (item.endDate === null) hasActiveRoles = true;
       
       if (start < minDate) minDate = start;
       if (end > maxDate) maxDate = end;

       // Parse payment orders to include them in the timeline range
       if (item.paymentOrders) {
           try {
               const orders = JSON.parse(item.paymentOrders);
               Object.keys(orders).forEach(dateStr => {
                   const t = new Date(dateStr).getTime();
                   if (t < minDate) minDate = t;
                   if (t > maxDate) maxDate = t;
               });
           } catch (e) {
               console.error("Failed to parse payment orders for date range");
           }
       }
  });

  if (hasActiveRoles) {
      maxDate = Math.max(maxDate, today);
  }
  
  // Apply padding same as charts did internally
  const range = maxDate - minDate;
  const padding = range > 0 ? range * 0.05 : 1000 * 60 * 60 * 24 * 30;
  
  const sharedMinDate = minDate - padding;
  const sharedMaxDate = hasActiveRoles ? today : maxDate + padding;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', overflowY: 'auto' }}>
      <PageHeader title={t('personSearch.title')} description={t('ui.personSearchDescription')} icon={<PersonSearchIcon />} />
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box component="form" onSubmit={(event) => { event.preventDefault(); handleSearch(); }} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label={t('personSearch.personalNumber')}
            variant="outlined"
            size="small"
            value={personalNumber}
            onChange={(e) => setPersonalNumber(e.target.value)}
            disabled={loading}
            placeholder="YYYYMMDDXXXX"
            sx={{ width: { xs: '100%', sm: 260 } }}
          />
          <Button 
            variant="contained" 
            type="submit"
            disabled={loading || !personalNumber.trim()}
            size="small"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PersonSearchIcon sx={{ fontSize: 20 }} />}
          >
            {t('common.actions.search')}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ marginBottom: 'var(--spacing-lg)' }}>
          {error}
        </Alert>
      )}

      {loading && <PageLoadingSpinner variant="inline" label={t('common.states.loading')} />}
      {results.length > 0 && <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: "var(--spacing-md)" }}>
        <Autocomplete multiple disableCloseOnSelect size="small" limitTags={2} options={roleOptions} value={selectedRoles}
          onChange={(_, roles) => setSelectedRoles(roles)} sx={{ width: 420, maxWidth: "100%" }}
          renderOption={(props, role, { selected }) => <li {...props}><Checkbox checked={selected} sx={{ mr: 1 }} />{role || t("personSearch.unknownRole")} ({results.filter(row => row.functionText === role).length})</li>}
          renderInput={params => <TextField {...params} label={t("personSearch.roles")} placeholder={t("personSearch.selectRoles")} />} />
        <Button size="small" onClick={() => setSelectedRoles(roleOptions)}>{t("personSearch.allRoles")}</Button>
        <Typography variant="caption" color="text.secondary">{t("personSearch.filteredCount", { shown: visibleResults.length, total: results.length })}</Typography>
      </Box>}
      {visibleResults.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 'var(--spacing-md)', minHeight: 0 }}>
          {/* Top: List/Table - Full Width */}
          <Paper sx={{ width: '100%', display: 'flex', flexDirection: 'column', maxHeight: '400px', flexShrink: 0 }} elevation={1}>
            <Box sx={{ p: 1, px: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.default', display: 'flex', alignItems: 'center' }}>
               <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{visibleResults[0].name}</Typography>
            </Box>
            <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
              <Table stickyHeader size="small" aria-label={t('personSearch.resultsLabel')}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>{t('personSearch.organization')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>{t('personSearch.function')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>{t('personSearch.start')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>{t('personSearch.end')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {t('personSearch.companyStatus')}
                        <Tooltip title={t('personSearch.companyStatusHelp')}>
                          <HelpOutlineIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {t('personSearch.debt')}
                        <Tooltip title={t('personSearch.debtHelp')}>
                          <HelpOutlineIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {t('personSearch.networkSize')}
                        <Tooltip title={t('personSearch.networkSizeHelp')}>
                          <HelpOutlineIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {t('personSearch.networkDebt')}
                        <Tooltip title={t('personSearch.networkDebtHelp')}>
                          <HelpOutlineIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {t('personSearch.networkBankruptcy')}
                        <Tooltip title={t('personSearch.networkBankruptcyHelp')}>
                          <HelpOutlineIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleResults.map((row, index) => {
                    const isFirstInGroup = index === 0 || visibleResults[index - 1].organizationNumber !== row.organizationNumber;
                    
                    let rowSpan = 1;
                    if (isFirstInGroup) {
                      for (let i = index + 1; i < visibleResults.length; i++) {
                        if (visibleResults[i].organizationNumber === row.organizationNumber) {
                          rowSpan++;
                        } else {
                          break;
                        }
                      }
                    }

                    return (
                      <TableRow
                        key={index}
                        sx={{ 
                          '&:last-child td, &:last-child th': { borderBottom: 0 },
                          '& td, & th': isFirstInGroup && index !== 0 ? { borderTop: '2px solid', borderColor: 'divider' } : {},
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        {isFirstInGroup && (
                          <TableCell rowSpan={rowSpan} sx={{ verticalAlign: 'top', py: 0.5, fontSize: '0.75rem' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>{row.organizationName}</Typography>
                              <Link 
                                href={`/companyassessment/${row.organizationNumber}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                sx={{ fontSize: '0.7rem' }}
                              >
                                {row.organizationNumber}
                              </Link>
                            </Box>
                          </TableCell>
                        )}
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>{row.functionText}</TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>{row.startDate ? new Date(row.startDate).toISOString().split('T')[0] : '-'}</TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>{row.endDate ? new Date(row.endDate).toISOString().split('T')[0] : '-'}</TableCell>
                        {isFirstInGroup && (
                          <>
                            <TableCell rowSpan={rowSpan} sx={{ verticalAlign: 'top', py: 0.5, fontSize: '0.75rem' }}>
                                {getCompanyStatus(row.bolagsverketState)}
                            </TableCell>
                            <TableCell rowSpan={rowSpan} sx={{ verticalAlign: 'top', py: 0.5, fontSize: '0.75rem', color: (row.currentKfmDebt !== null && row.currentKfmDebt > 0) ? 'error.main' : 'inherit' }}>
                                {row.currentKfmDebt !== null ? formatCurrency(row.currentKfmDebt, i18n.resolvedLanguage) : '-'}
                            </TableCell>
                            <TableCell rowSpan={rowSpan} sx={{ verticalAlign: 'top', py: 0.5, fontSize: '0.75rem' }}>
                                {row.networkCompanyCount}
                            </TableCell>
                            <TableCell rowSpan={rowSpan} sx={{ verticalAlign: 'top', py: 0.5, fontSize: '0.75rem', color: row.networkCompaniesActiveDebtCount > 0 ? 'error.main' : 'inherit' }}>
                                {row.networkCompaniesActiveDebtCount}
                            </TableCell>
                            <TableCell rowSpan={rowSpan} sx={{ verticalAlign: 'top', py: 0.5, fontSize: '0.75rem', color: row.networkCompaniesBankruptcyCount > 0 ? 'error.main' : 'inherit' }}>
                                {row.networkCompaniesBankruptcyCount}
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          
          {/* Bottom: Graphs */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 'var(--spacing-md)', flexGrow: 1, alignItems: 'stretch' }}>
            {/* Left Column: Timeline Charts (Aligned) */}
            <Box sx={{ flex: 2, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
               
               <ExportableChart title={t('personSearch.timelineTitle')} subtitle={visibleResults[0].name} filename="engagemang-och-skulder">
                       <EngagementDebtChart data={visibleResults} minDate={sharedMinDate} maxDate={sharedMaxDate} />
               </ExportableChart>

            </Box>

            {/* Right Column: Workload */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <ExportableChart title={t('personSearch.charts.workload')} subtitle={visibleResults[0].name} filename="aktiva-uppdrag">
                      <WorkloadChart data={visibleResults} />
                </ExportableChart>
            </Box>
          </Box>
        </Box>
      ) : (
        !loading && !error && (
          <Paper variant="outlined"><EmptyState icon={<PersonSearchIcon />} title={t(results.length ? 'personSearch.noMatchingRoles' : hasSearched ? 'personSearch.noResults' : 'ui.personSearchStart')} description={t('ui.personSearchHelp')} /></Paper>
        )
      )}
    </Box>
  );
};

export default PersonSearch;
